import {
  DarwinFlex,
  DMenuProps,
  DTableProps,
  StatusChip,
  StatusChipColors,
  renderUserCell,
  renderDateCell,
  ActionsDropdown,
  CombinedText,
  STATUS_COL_WIDTH,
  ACTIONS_COL_WIDTH,
} from '@darwin-ui';
import { Checkbox } from 'antd';
import { BaseProduct, Product, Variety } from 'types/productsTypes';
import { UserList } from 'types/userTypes';
import Icon from 'components/darwin-ui/Icon';
import { TFunction } from 'i18next';
import { ColumnAction } from '.';
import { translateProductLabel } from 'utils/product';

export const getProductColumns = (
  t: TFunction,
  actions: ColumnAction[],
  searchQuery: string
) => {
  const menuItems = (product: Product): DMenuProps['items'] =>
    actions.map(action => ({
      key: action.key,
      label:
        action.key === 'activate-deactivate' ? (
          <div onClick={() => action?.onClick?.(product)}>
            <StatusChip
              label={
                product.active
                  ? t('product.columns.deactivate')
                  : t('product.columns.activate')
              }
              color={
                product.active ? StatusChipColors.red : StatusChipColors.green
              }
            />
          </div>
        ) : (
          <DarwinFlex
            gap={8}
            align="center"
            onClick={() => action?.onClick?.(product)}
          >
            <Icon
              name={action.name ? action.name : 'darwin-user'}
              size="sm"
              color="inherit"
            />
            {action.label}
          </DarwinFlex>
        ),
    }));

  const renderUserCol = (user: string) => renderUserCell(user, searchQuery);

  const columns: DTableProps<Product>['columns'] = [
    {
      title: t('product.columns.product-name'),
      dataIndex: 'name',
      key: 'name',
      sorter: true,
      render: (name: string) => (
        <CombinedText lines={1} text={name} highlight={searchQuery} />
      ),
      width: 320,
    },
    {
      title: t('product.columns.description'),
      dataIndex: 'description',
      key: 'description',
      sorter: true,
      render: (description: string) => (
        <CombinedText lines={1} text={description} highlight={searchQuery} />
      ),
    },
    {
      title: t('product.columns.traceability-product-code'),
      dataIndex: 'traceabilityProductCode',
      key: 'traceabilityProductCode',
      sorter: true,
      align: 'center',
      render: (traceabilityProductCode: string) => (
        <CombinedText
          lines={1}
          text={traceabilityProductCode}
          highlight={searchQuery}
        />
      ),
      width: 200,
    },
    {
      title: t('product.columns.product-id'),
      dataIndex: 'externalId',
      key: 'externalId',
      sorter: true,
      align: 'center',
      render: (externalId: string) => (
        <CombinedText lines={1} text={externalId} highlight={searchQuery} />
      ),
      width: 320,
    },
    {
      title: t('product.columns.product-status'),
      dataIndex: 'active',
      key: 'active',
      sorter: true,
      align: 'center',
      render: (status: boolean) => (
        <StatusChip
          color={status ? StatusChipColors.green : StatusChipColors.red}
          label={
            status ? t('product.columns.active') : t('product.columns.inactive')
          }
        />
      ),
      width: STATUS_COL_WIDTH,
    },
    {
      title: t('product.columns.sku'),
      dataIndex: 'sku',
      key: 'sku',
      sorter: true,
      align: 'center',
      render: (sku: string) => (
        <CombinedText truncate={true} text={sku} highlight={searchQuery} />
      ),
      width: 140,
    },
    {
      title: t('product.columns.gtin'),
      dataIndex: 'gtin',
      key: 'gtin',
      sorter: true,
      align: 'center',
      render: (gtin: string) => (
        <CombinedText truncate={true} text={gtin} highlight={searchQuery} />
      ),
      width: 140,
    },
    {
      title: t('product.columns.product'),
      dataIndex: 'product',
      key: 'category',
      sorter: true,
      render: (product: BaseProduct) => {
        const variety = product?.variety as Variety;

        if (variety) {
          return (
            <CombinedText
              truncate={true}
              text={translateProductLabel(
                t,
                'variety',
                variety.translationKey,
                variety.name
              )}
              highlight={searchQuery}
            />
          );
        }
        return (
          <CombinedText truncate={true} text="N/A" highlight={searchQuery} />
        );
      },
      width: 320,
      align: 'center',
    },
    {
      title: t('product.columns.final-product'),
      dataIndex: 'finalProduct',
      key: 'finalProduct',
      sorter: true,
      align: 'center',
      render: (finalProduct: boolean) => (
        <Checkbox disabled={true} checked={finalProduct} />
      ),
    },
    {
      title: t('product.columns.created-by'),
      dataIndex: 'createdBy',
      key: 'createdBy',
      sorter: true,
      render: (record: UserList) => {
        if (!record) return renderUserCell('N/A', searchQuery);
        const name = `${record.firstName || ''} ${record.lastName || ''}`;
        return renderUserCol(name);
      },
    },
    {
      title: t('product.columns.created-date'),
      dataIndex: 'createdAt',
      key: 'createdAt',
      sorter: true,
      render: (createdAt: string) => (
        <CombinedText
          lines={1}
          text={renderDateCell(createdAt)}
          highlight={searchQuery}
        />
      ),
      align: 'center',
    },
    {
      title: t('product.columns.modified-by'),
      dataIndex: 'updatedBy',
      key: 'updatedBy',
      sorter: true,
      render: (record: UserList) => {
        if (!record) return renderUserCol('');
        const name = `${record.firstName || ''} ${record.lastName || ''}`;
        return renderUserCol(name);
      },
    },
    {
      title: t('product.columns.modified-date'),
      dataIndex: 'updatedAt',
      key: 'updatedAt',
      sorter: true,
      render: (updatedAt: string) => (
        <CombinedText
          lines={1}
          text={renderDateCell(updatedAt)}
          highlight={searchQuery}
        />
      ),
      align: 'center',
      width: 200,
    },
    {
      dataIndex: 'actions',
      key: 'actions',
      fixed: 'right',
      align: 'center',
      render: (_, product) => (
        <ActionsDropdown item={product} getMenuItems={menuItems} />
      ),
      width: ACTIONS_COL_WIDTH,
    },
  ];

  return columns;
};
